// Поиск и замена по документу (Ctrl+F / Ctrl+H): регистр, целое слово, регулярные выражения; подсветка всех
// совпадений, «3 из 15», замена одного и всех (одной правкой - отменяется Ctrl+Z целиком).
// Поиск идёт внутри текстовых блоков (абзацы, заголовки, подписи, ячейки); формулы и ссылки - один символ-объект.
import { useEffect, useRef, useState } from 'react';
import { Extension, type Editor } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection, type EditorState, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';
import { uiStore, useUi, type UiState } from '../../ui/uiState';
import { appStore } from '../../core/store';
import { Icon } from '../../ui/icons';
import { ctx } from '../../core/ctx';

export interface FindSpec { query: string; caseSensitive: boolean; wholeWord: boolean; regex: boolean }
export interface Match { from: number; to: number; text: string }
interface FindState { spec: FindSpec | null; matches: Match[]; current: number; deco: DecorationSet; error: string | null }

export const findKey = new PluginKey<FindState>('stoFind');
const MAX_MATCHES = 5000;
const OBJ = '￼';

/** Регулярное выражение по параметрам поиска (или ошибка в синтаксисе). */
export function buildRegex(spec: FindSpec): RegExp | string | null {
  if (!spec.query) return null;
  let src = spec.regex ? spec.query : spec.query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (spec.wholeWord) src = `(?<![\\p{L}\\p{N}_])(?:${src})(?![\\p{L}\\p{N}_])`;
  try { return new RegExp(src, 'gu' + (spec.caseSensitive ? '' : 'i')); } catch (e: any) { return 'Ошибка в выражении: ' + (e?.message || e); }
}

export function findMatches(doc: PMNode, re: RegExp): Match[] {
  const out: Match[] = [];
  doc.descendants((node, pos) => {
    if (out.length >= MAX_MATCHES) return false;
    if (!node.isTextblock) return true;
    const text = node.textBetween(0, node.content.size, undefined, OBJ);
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) && out.length < MAX_MATCHES) {
      if (!m[0].length) { re.lastIndex++; continue; }
      if (m[0].includes(OBJ)) continue;          // совпадение не должно захватывать формулу/ссылку
      out.push({ from: pos + 1 + m.index, to: pos + 1 + m.index + m[0].length, text: m[0] });
    }
    return false;
  });
  return out;
}

function decorate(doc: PMNode, matches: Match[], current: number) {
  return DecorationSet.create(doc, matches.map((m, i) => Decoration.inline(m.from, m.to, { class: i === current ? 'sto-find sto-find-cur' : 'sto-find' })));
}

function nearest(matches: Match[], pos: number) {
  const i = matches.findIndex((m) => m.from >= pos);
  return i < 0 ? (matches.length ? 0 : -1) : i;
}

function compute(state: EditorState, spec: FindSpec | null, prevCurrent: number | null): FindState {
  if (!spec || !spec.query) return { spec, matches: [], current: -1, deco: DecorationSet.empty, error: null };
  const re = buildRegex(spec);
  if (typeof re === 'string') return { spec, matches: [], current: -1, deco: DecorationSet.empty, error: re };
  const matches = re ? findMatches(state.doc, re) : [];
  const current = prevCurrent !== null && prevCurrent < matches.length ? prevCurrent : nearest(matches, state.selection.from);
  return { spec, matches, current, deco: decorate(state.doc, matches, current), error: null };
}

export const FindExtension = Extension.create({
  name: 'stoFind',
  addProseMirrorPlugins() {
    return [new Plugin<FindState>({
      key: findKey,
      state: {
        init: () => ({ spec: null, matches: [], current: -1, deco: DecorationSet.empty, error: null }),
        apply(tr: Transaction, prev, _old, state) {
          const meta = tr.getMeta(findKey) as { spec?: FindSpec | null; current?: number } | undefined;
          if (meta && 'spec' in meta) return compute(state, meta.spec ?? null, null);
          if (meta && meta.current !== undefined) {
            return { ...prev, current: meta.current, deco: decorate(state.doc, prev.matches, meta.current) };
          }
          if (tr.docChanged && prev.spec?.query) return compute(state, prev.spec, prev.current);
          return prev;
        },
      },
      props: { decorations: (s) => findKey.getState(s)?.deco },
    })];
  },
});

// ------------------------------------------------------------------------------------------------ действия
const specOf = (f: UiState['find']): FindSpec => ({ query: f.query, caseSensitive: f.caseSensitive, wholeWord: f.wholeWord, regex: f.regex });
export function pushSpec(editor: Editor | null) {
  if (!editor || editor.isDestroyed) return;
  const f = uiStore.getState().find;
  editor.view.dispatch(editor.state.tr.setMeta(findKey, { spec: f.open ? specOf(f) : null }).setMeta('addToHistory', false));
}

function select(editor: Editor, i: number) {
  const st = findKey.getState(editor.state);
  if (!st || i < 0 || i >= st.matches.length) return;
  const m = st.matches[i];
  const tr = editor.state.tr.setMeta(findKey, { current: i }).setSelection(TextSelection.create(editor.state.doc, m.from, m.to))
    .setMeta('addToHistory', false).scrollIntoView();
  editor.view.dispatch(tr);
  // выделенное совпадение - в середину экрана
  requestAnimationFrame(() => {
    try {
      const dom = editor.view.domAtPos(m.from).node as HTMLElement;
      const el = (dom.nodeType === 3 ? dom.parentElement : dom) as HTMLElement | null;
      el?.scrollIntoView({ block: 'center' });
    } catch { /* нет узла */ }
  });
}

export function findStep(editor: Editor | null, dir: 1 | -1) {
  if (!editor) return;
  const st = findKey.getState(editor.state);
  if (!st || !st.matches.length) return;
  let i: number;
  const sel = editor.state.selection;
  const onCur = st.current >= 0 && st.matches[st.current] && st.matches[st.current].from === sel.from && st.matches[st.current].to === sel.to;
  if (onCur) i = (st.current + dir + st.matches.length) % st.matches.length;
  else if (dir > 0) { i = st.matches.findIndex((m) => m.from >= sel.to); if (i < 0) i = 0; }
  else { i = -1; for (let k = st.matches.length - 1; k >= 0; k--) if (st.matches[k].to <= sel.from) { i = k; break; } if (i < 0) i = st.matches.length - 1; }
  select(editor, i);
}

function replacementFor(m: Match, spec: FindSpec, repl: string): string {
  if (!spec.regex) return repl;
  const re = buildRegex(spec);
  if (!(re instanceof RegExp)) return repl;
  return m.text.replace(new RegExp(re.source, re.flags.replace('g', '')), repl);
}

export function replaceCurrent(editor: Editor | null) {
  if (!editor) return;
  const st = findKey.getState(editor.state);
  const f = uiStore.getState().find;
  if (!st || !st.spec || !st.matches.length) return;
  const sel = editor.state.selection;
  const i = st.matches.findIndex((m) => m.from === sel.from && m.to === sel.to);
  if (i < 0) { findStep(editor, 1); return; }
  const m = st.matches[i];
  const text = replacementFor(m, st.spec, f.replaceWith);
  const tr = editor.state.tr;
  if (text) tr.insertText(text, m.from, m.to); else tr.delete(m.from, m.to);
  editor.view.dispatch(tr);
  findStep(editor, 1);
}

export function replaceAll(editor: Editor | null): number {
  if (!editor) return 0;
  const st = findKey.getState(editor.state);
  const f = uiStore.getState().find;
  if (!st || !st.spec || !st.matches.length) return 0;
  const tr = editor.state.tr;
  for (let k = st.matches.length - 1; k >= 0; k--) {
    const m = st.matches[k];
    const text = replacementFor(m, st.spec, f.replaceWith);
    if (text) tr.insertText(text, m.from, m.to); else tr.delete(m.from, m.to);
  }
  editor.view.dispatch(tr);
  return st.matches.length;
}

/** Открыть поиск (replace - с заменой); выделенный текст подставляется в строку поиска. */
export function openFind(replace: boolean) {
  const e = appStore.getState().editor;
  const f = uiStore.getState().find;
  let query = f.query;
  if (e && !e.state.selection.empty) {
    const t = e.state.doc.textBetween(e.state.selection.from, e.state.selection.to, ' ');
    if (t && t.length < 200 && !t.includes('\n')) query = t;
  }
  uiStore.getState().set({ find: { ...f, open: true, replace: replace || (f.open && f.replace), query } });
  pushSpec(e);
  setTimeout(() => {
    const inp = document.querySelector('[data-testid=find-input]') as HTMLInputElement | null;
    inp?.focus(); inp?.select();
  }, 0);
}

export function closeFind() {
  const f = uiStore.getState().find;
  uiStore.getState().set({ find: { ...f, open: false } });
  const e = appStore.getState().editor;
  pushSpec(e);
  e?.commands.focus();
}

// ------------------------------------------------------------------------------------------------ панель
export function FindBar() {
  const f = useUi((s) => s.find);
  const editor = appStore.getState().editor;
  const [, bump] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const replaceRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!editor) return;
    const on = () => bump((x) => x + 1);
    editor.on('transaction', on);
    return () => { editor.off('transaction', on); };
  }, [editor]);
  if (!f.open) return null;
  const st = editor ? findKey.getState(editor.state) : null;
  const n = st?.matches.length || 0;
  const cur = st && st.current >= 0 ? st.current + 1 : 0;
  const upd = (p: Partial<UiState['find']>) => {
    uiStore.getState().set({ find: { ...uiStore.getState().find, ...p } });
    pushSpec(appStore.getState().editor);
  };
  const toggle = (k: 'caseSensitive' | 'wholeWord' | 'regex', icon: string, title: string) => (
    <button type="button" tabIndex={-1} className={'find-opt' + (f[k] ? ' is-active' : '')} aria-pressed={f[k]} data-tip={title}
      data-testid={'find-' + k} onMouseDown={(e) => e.preventDefault()} onClick={() => upd({ [k]: !f[k] } as any)}><Icon name={icon} size={16} /></button>
  );
  return (
    <div className="find-bar" role="search" data-testid="find-bar"
      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeFind(); } }}>
      <button type="button" className="find-expand" aria-label="Замена" data-tip={f.replace ? 'Скрыть замену' : 'Заменить'} data-tip-key="Ctrl+H"
        onClick={() => uiStore.getState().set({ find: { ...f, replace: !f.replace } })}>
        <Icon name={f.replace ? 'down' : 'chevron-right'} size={14} />
      </button>
      <div className="find-rows">
        <div className="find-row">
          <div className={'find-input-wrap' + (st?.error ? ' is-bad' : '')}>
            <Icon name="search" size={14} />
            <input ref={inputRef} value={f.query} placeholder="Найти в документе" data-testid="find-input" aria-label="Найти"
              onChange={(e) => upd({ query: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); findStep(appStore.getState().editor, e.shiftKey ? -1 : 1); }
                // Tab - сразу в «Заменить на» (как в Word), а не по переключателям параметров
                if (e.key === 'Tab' && !e.shiftKey && f.replace) { const r = replaceRef.current; if (r) { e.preventDefault(); r.focus(); r.select(); } }
              }} />
            {toggle('caseSensitive', 'case', 'Учитывать регистр')}
            {toggle('wholeWord', 'whole-word', 'Только слово целиком')}
            {toggle('regex', 'regex', 'Регулярное выражение')}
          </div>
          <span className={'find-count' + (f.query && !n ? ' is-none' : '')} data-testid="find-count" title={st?.error || ''}>
            {st?.error ? 'ошибка' : f.query ? (n ? `${cur || '–'} из ${n}${n >= MAX_MATCHES ? '+' : ''}` : 'нет') : ''}
          </span>
          <button type="button" className="find-btn" data-tip="Предыдущее" data-tip-key="Shift+Enter" disabled={!n}
            onClick={() => findStep(appStore.getState().editor, -1)} data-testid="find-prev"><Icon name="chevron-up" size={16} /></button>
          <button type="button" className="find-btn" data-tip="Следующее" data-tip-key="Enter" disabled={!n}
            onClick={() => findStep(appStore.getState().editor, 1)} data-testid="find-next"><Icon name="down" size={16} /></button>
          <button type="button" className="find-btn" data-tip="Закрыть" data-tip-key="Esc" onClick={closeFind} data-testid="find-close"><Icon name="close" size={16} /></button>
        </div>
        {f.replace && (
          <div className="find-row">
            <div className="find-input-wrap">
              <Icon name="replace" size={14} />
              <input ref={replaceRef} value={f.replaceWith} placeholder={f.regex ? 'Заменить на ($1 - группа)' : 'Заменить на'} data-testid="replace-input"
                aria-label="Заменить на" onChange={(e) => uiStore.getState().set({ find: { ...uiStore.getState().find, replaceWith: e.target.value } })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); if (e.ctrlKey) replaceAll(appStore.getState().editor); else replaceCurrent(appStore.getState().editor); }
                  if (e.key === 'Tab' && e.shiftKey && inputRef.current) { e.preventDefault(); inputRef.current.focus(); inputRef.current.select(); }
                }} />
            </div>
            <button type="button" className="find-text-btn" disabled={!n} onClick={() => replaceCurrent(appStore.getState().editor)}
              data-testid="replace-one" data-tip="Заменить это совпадение и перейти к следующему" data-tip-key="Enter">Заменить</button>
            <button type="button" className="find-text-btn" disabled={!n} data-testid="replace-all" data-tip-key="Ctrl+Enter"
              data-tip="Заменить все совпадения (одна правка - Ctrl+Z отменит всё)"
              onClick={() => { const k = replaceAll(appStore.getState().editor); ctx.toast(k ? `Заменено: ${k}` : 'Нечего заменять', k ? 'ok' : 'info', 2500); }}>Заменить все</button>
          </div>
        )}
      </div>
    </div>
  );
}
